import { useCanViewEmails } from '../../../../../helpers/useCanViewEmails';
import React, { useState } from 'react';
import Select from '../../../AccessSelect';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Tooltip } from '@mui/material';
import cs from 'classnames';

import {
  FILE_ACCESS_TYPE,
  FILE_ACCESS_TYPE_SHARED_WITH_ME_WITH_SUBGROUP,
  GROUP,
  GROUP_TYPE,
  GROUP_WITH_SUBGROUP,
} from '../../../../../enums';
import { ACCESS_TYPE, ACCESS_TYPE_ENUM, ACCESS_TYPE_KEYS } from '../../../../../enums/ACCESS_ENUM';
import TrashButton from '../../../../modules/TrashButton';
import { AccessMapJson, DownloadType, NewAccessType, RenotifyIdType } from '../../../MyFolderPage/ShareFolderModal';
import { RENOTIFY_BODY } from '../../../../../enums/NOTIFICATION';
import BellIconFilled from '../../../../../assets/icons/bell-button-filled.svg';
import BellIcon from '../../../../../assets/icons/share-notify-off.svg';
import css from '../ShareFolderModal.module.scss';
import { GROUP_SHARE_OPTIONS, SUB_GROUP_SHARE_OPTIONS } from '../../../MyFolderPage/ShareFolderModal/GroupList';
import { getDefaultShareAllowDownloads } from '../../../../../helpers';
import UsersIcon from '../../../../../assets/icons/users.svg';

type TGroupList = {
  newGroups: GROUP[];
  groupList: FILE_ACCESS_TYPE_SHARED_WITH_ME_WITH_SUBGROUP[];
  newExcludedGroups?: GROUP[];
  excludedUsersAccessList?: FILE_ACCESS_TYPE_SHARED_WITH_ME_WITH_SUBGROUP[];
  subGroupList: FILE_ACCESS_TYPE_SHARED_WITH_ME_WITH_SUBGROUP[];
  onNewGroupDownloadPermissionChange?: (userId: string, allowDownload: boolean) => void;
  newGroupAccess?: NewAccessType | null;
  downloadPermissions?: DownloadType;
  onDownloadPermissionChange?: (accessObject: any, accessId: string, allowDownload: boolean) => void;
  onGroupAccessChange: (id: string, e: any, groupId: string, groupType: any, accessObject?: any) => void;
  onNewGroupAccessChange: (id: string, e: any, groupType: any, groupObj?: any) => void;
  onAccessDelete: (accessId: string, accessType?: 'group' | 'subgroup') => void;
  onNewGroupDelete: (groupId: string) => void;
  addEntityToRenotify: (id: string, type: RenotifyIdType, includeSubgroups?: GROUP) => void;
  idsToRenotify: RENOTIFY_BODY | null;
  accessMapJson: AccessMapJson[];
};
const ShareFolderModalGroupList: React.FC<TGroupList> = ({
  groupList,
  newGroups,
  newExcludedGroups,
  excludedUsersAccessList,
  onGroupAccessChange,
  onNewGroupAccessChange,
  onNewGroupDownloadPermissionChange,
  onDownloadPermissionChange,
  subGroupList,
  downloadPermissions,
  newGroupAccess,
  onAccessDelete,
  onNewGroupDelete,
  addEntityToRenotify,
  idsToRenotify,
  accessMapJson,
}) => {
  const canViewEmails = useCanViewEmails();
  const [expanded, setExpanded] = useState<string | false>(false);

  const isGroupExcluded = (groupId: string) =>
    (newExcludedGroups?.some((g) => g.id === groupId) || excludedUsersAccessList?.some(({ group }) => group?.id === groupId)) ?? false;

  const findExistingSubAccess = (subId: string) => subGroupList.find((a) => a.group?.id === subId) || null;

  const isNoAccessMapped = (groupId: string, subId: string) =>
    getAccessFromMap(groupId, subId) === ACCESS_TYPE_ENUM.WITHOUT_ACCESS || getAccessFromMap(groupId, subId) === ACCESS_TYPE_ENUM.NO_ACCESS;

  const getAccessFromMap = (groupId: string, subId?: string): string | undefined => {
    const g = accessMapJson.find((x) => x.id === groupId);
    if (!g) return undefined;
    if (!subId) return g.access;
    return g.subGroups?.find((s) => s.id === subId)?.access;
  };

  const groupUsersOrganizer = (group: GROUP) => {
    return [...(group?.analysts || []), ...(group?.viewers || [])].filter(
      (value, index, self) => index === self.findIndex((user) => user.id === value.id),
    );
  };

  const getEffectiveAllowDownloads = ({
    existing,
    id,
    parentGroupId,
  }: {
    existing: FILE_ACCESS_TYPE | FILE_ACCESS_TYPE_SHARED_WITH_ME_WITH_SUBGROUP | null | undefined;
    id: string;
    parentGroupId?: string;
  }) => {
    if (existing?.id) {
      const override = downloadPermissions?.[existing.id]?.allowDownload;
      if (typeof override === 'boolean') return override;
      return !!existing.allow_downloads;
    }

    const draft = newGroupAccess?.[id];
    if (typeof draft?.allowDownloads === 'boolean') return draft.allowDownloads;

    const mapped = parentGroupId ? getAccessFromMap(parentGroupId, id) : getAccessFromMap(id);
    if (mapped) {
      return mapped !== ACCESS_TYPE_ENUM.WITHOUT_ACCESS && mapped !== ACCESS_TYPE_ENUM.NO_ACCESS;
    }

    return true;
  };

  const handleChange = (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    if (!event.currentTarget.className.includes('Mui-focusVisible')) {
      setExpanded(isExpanded ? panel : false);
    }
  };

  const groupAccess = (accessObject: FILE_ACCESS_TYPE_SHARED_WITH_ME_WITH_SUBGROUP) => {
    let result =
      GROUP_SHARE_OPTIONS[
        GROUP_SHARE_OPTIONS.findIndex((option) => {
          return option.value === accessObject?.access?.title;
        })
      ];

    if (!result) {
      return {
        value: ACCESS_TYPE_ENUM.SUBGROUP_SPECIFIC_ACCESS,
        label: ACCESS_TYPE.SUBGROUP_SPECIFIC_ACCESS,
      };
    }
    return result;
  };

  return (
    <div className={css.list}>
      {/* New selected groups */}
      {newGroups.map((group: GROUP) => {
        const isExcludedGroup =
          newExcludedGroups?.map((g) => g.id).includes(group.id) ||
          excludedUsersAccessList?.map(({ group }) => group.id).includes(group.id);

        return (
          <Accordion
            sx={{
              padding: 0,
              marginBottom: '8px',
              backgroundColor: '#0f2336',
              color: 'white',
              boxShadow: 'none',
              borderRadius: '4px',
              opacity: isExcludedGroup ? '50%' : '',
              pointerEvents: isExcludedGroup ? 'none' : 'auto',
            }}
            key={group.id}
            expanded={expanded === group?.id}
            className={css.group}
            onChange={handleChange(group?.id || 'emptyId')}
          >
            <AccordionSummary
              sx={{
                '& > .MuiAccordionSummary-content': {
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                },
                '& > .MuiAccordionSummary-expandIconWrapper': {
                  color: '#868686',
                  marginLeft: '8px',
                },
              }}
              expandIcon={<ExpandMoreIcon />}
            >
              <div className={css.title}>{group?.title}</div>
              <div className={css.selectorContainer}>
                <Tooltip
                  placement="right"
                  arrow
                  title={
                    <div className={css.tooltip}>
                      {' '}
                      {groupUsersOrganizer(group).map((u) => (
                        <div key={`${u.id}_tooltip`} style={{ display: 'flex', justifyContent: 'space-between' }}>
                          {' '}
                          <span style={{ marginRight: '8px' }}>{u?.username ? u.username : (canViewEmails ? u.email : 'User')} </span>
                        </div>
                      ))}{' '}
                    </div>
                  }
                >
                  <div className={css.data}>
                    <img src={UsersIcon} alt="users" />
                    {groupUsersOrganizer(group).length}
                  </div>
                </Tooltip>
                <Select
                  isSearchable={false}
                  defaultValue={GROUP_SHARE_OPTIONS[2]}
                  value={
                    newExcludedGroups?.map((group) => group.id).includes(group.id) ||
                    excludedUsersAccessList?.map(({ group }) => group?.id).includes(group.id)
                      ? {
                          value: ACCESS_TYPE_ENUM.NO_ACCESS,
                          label: ACCESS_TYPE.NO_ACCESS,
                        }
                      : (accessMapJson.length &&
                          GROUP_SHARE_OPTIONS.find((opt) => opt.value === accessMapJson.find((g) => g.id === group.id)?.access)) ?? {
                          value: ACCESS_TYPE_ENUM.NO_ACCESS,
                          label: ACCESS_TYPE.NO_ACCESS,
                        }
                  }
                  styles={{
                    control: (baseStyles) => ({
                      ...baseStyles,
                      background: '#525252',
                      border: '1px solid #DBDBDB',
                      borderRadius: '4px',
                      color: '#DBDBDB',
                      height: '24px',
                      padding: '0 8px',
                      marginRight: '20px',
                    }),
                  }}
                  options={
                    group?.subgroups?.length
                      ? GROUP_SHARE_OPTIONS
                      : GROUP_SHARE_OPTIONS.filter((o) => o.value !== ACCESS_TYPE_ENUM.SUBGROUP_SPECIFIC_ACCESS)
                  }
                  unstyled
                  className={css.customSelector}
                  classNamePrefix="select"
                  name="access"
                  onChange={(e) => onNewGroupAccessChange(group.id, e, GROUP_TYPE.GROUP, group)}
                  menuPosition={'fixed'}
                />

                {!!(onNewGroupDownloadPermissionChange && newGroupAccess) && (
                  <div className={css.allowDownloadCheckbox}>
                    <div className={css.checkboxWrapper30}>
                      <div className={css.allowDownloadCheckboxText}>
                        Allow
                        <br />
                        Downloads?
                      </div>
                      <span className={css.checkbox}>
                        <input
                          type="checkbox"
                          onClick={(e) => e.stopPropagation()}
                          onChange={() =>
                            onNewGroupDownloadPermissionChange(
                              group.id,
                              !(newGroupAccess?.[group.id]?.allowDownloads ?? getDefaultShareAllowDownloads()),
                            )
                          }
                          checked={
                            isExcludedGroup
                              ? false
                              : newGroupAccess?.[group.id]?.allowDownloads ?? getDefaultShareAllowDownloads()
                          }
                        />
                        <svg>
                          <use xlinkHref="#checkbox-30" className={css.checkbox}></use>
                        </svg>
                      </span>
                      <svg xmlns="http://www.w3.org/2000/svg" style={{ display: 'none' }}>
                        <symbol id="checkbox-30" viewBox="0 0 22 22">
                          <path
                            fill="none"
                            stroke="currentColor"
                            d="M5.5,11.3L9,14.8L20.2,3.3l0,0c-0.5-1-1.5-1.8-2.7-1.8h-13c-1.7,0-3,1.3-3,3v13c0,1.7,1.3,3,3,3h13 c1.7,0,3-1.3,3-3v-13c0-0.4-0.1-0.8-0.3-1.2"
                          />
                        </symbol>
                      </svg>
                    </div>
                  </div>
                )}

                <div className={css.actionButtons}>
                  <div className={css.removeBtn}>
                    <Tooltip title={'Delete'}>
                      <TrashButton
                        small
                        onClick={(e: any) => {
                          e.stopPropagation();
                          onNewGroupDelete(group.id);
                        }}
                      />
                    </Tooltip>
                  </div>
                </div>
              </div>
            </AccordionSummary>

            {/* New selected sub groups */}
            <AccordionDetails sx={{ borderTop: '0.5px solid #868686' }}>
              {group?.subgroups?.map((subGroup) => {
                const isExcludedSubgroup =
                  isExcludedGroup ||
                  newExcludedGroups?.map((g) => g.id).includes(subGroup.id) ||
                  excludedUsersAccessList?.map(({ group }) => group.id).includes(subGroup.id);

                const isNoAccess = isNoAccessMapped(group.id, subGroup.id);

                return (
                  <div
                    key={subGroup.id}
                    style={{
                      opacity: isExcludedSubgroup ? '50%' : '',
                      pointerEvents: isExcludedSubgroup ? 'none' : 'auto',
                    }}
                    className={css.subGroup}
                  >
                    <div className={css.subgroupContent}>
                      <span className={css.subGroupTitle}>{subGroup.title}</span>
                      <div className={css.selectorContainer}>
                        <Tooltip
                          placement="right"
                          arrow
                          title={
                            <div className={css.tooltip}>
                              {' '}
                              {groupUsersOrganizer(subGroup).map((u) => (
                                <div key={`${u.id}_tooltip`} style={{ display: 'flex', justifyContent: 'space-between' }}>
                                  {' '}
                                  <span style={{ marginRight: '8px' }}>{u?.username ? u.username : (canViewEmails ? u.email : 'User')} </span>{' '}
                                </div>
                              ))}{' '}
                            </div>
                          }
                        >
                          <div className={css.data}>
                            <img src={UsersIcon} alt="users" />
                            {groupUsersOrganizer(subGroup).length}
                          </div>
                        </Tooltip>
                        <Select
                          isSearchable={false}
                          value={
                            isExcludedSubgroup
                              ? {
                                  value: ACCESS_TYPE_ENUM.NO_ACCESS,
                                  label: ACCESS_TYPE.NO_ACCESS,
                                }
                              : (accessMapJson.length &&
                                  SUB_GROUP_SHARE_OPTIONS.find(
                                    (opt) =>
                                      opt.value ===
                                      accessMapJson.find((g) => g.id === group.id)?.subGroups?.find((ac) => ac.id === subGroup.id)?.access,
                                  )) ?? {
                                  value: ACCESS_TYPE_ENUM.NO_ACCESS,
                                  label: ACCESS_TYPE.NO_ACCESS,
                                }
                          }
                          styles={{
                            control: (baseStyles) => ({
                              ...baseStyles,
                              background: '#525252',
                              border: '1px solid #DBDBDB',
                              borderRadius: '4px',
                              color: '#DBDBDB',
                              height: '24px',
                              padding: '0 8px',
                            }),
                          }}
                          options={SUB_GROUP_SHARE_OPTIONS}
                          unstyled
                          className={css.customSelector}
                          classNamePrefix="select"
                          name="access"
                          onChange={(e) =>
                            subGroupList.find((subgroup) => subgroup?.group?.id === subGroup.id)?.id
                              ? onGroupAccessChange(
                                  subGroupList.find((subgroup) => subgroup?.group?.id === subGroup.id)!.id,
                                  e,
                                  subGroup.id,
                                  GROUP_TYPE.SUBGROUP,
                                )
                              : onNewGroupAccessChange(subGroup.id, e, GROUP_TYPE.SUBGROUP)
                          }
                          menuPosition={'fixed'}
                        />
                      </div>
                    </div>

                    {!!(onNewGroupDownloadPermissionChange && newGroupAccess) && (
                      <div className={css.allowDownloadCheckbox}>
                        <div className={css.checkboxWrapper30}>
                          <div className={css.allowDownloadCheckboxText}>
                            Allow
                            <br />
                            Downloads?
                          </div>
                          <span className={css.checkbox}>
                            <input
                              type="checkbox"
                              onClick={(e) => e.stopPropagation()}
                              disabled={isNoAccess}
                              className={isNoAccess ? css.disableElementWrapper : ''}
                              onChange={() => {
                                if (isNoAccess) return;
                                const next = !getEffectiveAllowDownloads({
                                  existing: undefined,
                                  id: subGroup.id,
                                  parentGroupId: group.id,
                                });
                                onNewGroupDownloadPermissionChange(subGroup.id, next);
                              }}
                              checked={
                                isExcludedSubgroup
                                  ? false
                                  : isNoAccess
                                  ? false
                                  : getEffectiveAllowDownloads({
                                      existing: undefined,
                                      id: subGroup.id,
                                      parentGroupId: group.id,
                                    })
                              }
                            />
                            <svg>
                              <use xlinkHref="#checkbox-30" className={css.checkbox}></use>
                            </svg>
                          </span>
                          <svg xmlns="http://www.w3.org/2000/svg" style={{ display: 'none' }}>
                            <symbol id="checkbox-30" viewBox="0 0 22 22">
                              <path
                                fill="none"
                                stroke="currentColor"
                                d="M5.5,11.3L9,14.8L20.2,3.3l0,0c-0.5-1-1.5-1.8-2.7-1.8h-13c-1.7,0-3,1.3-3,3v13c0,1.7,1.3,3,3,3h13 c1.7,0,3-1.3,3-3v-13c0-0.4-0.1-0.8-0.3-1.2"
                              />
                            </symbol>
                          </svg>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </AccordionDetails>
          </Accordion>
        );
      })}

      {/* Already granted group accesses */}
      {groupList.map(
        (accessObject) =>
          accessObject?.group && (
            <Accordion
              sx={{
                padding: 0,
                backgroundColor: '#0f2336',
                color: 'white',
                boxShadow: 'none',
                borderRadius: '4px',
                opacity: isGroupExcluded(accessObject.group.id) ? '50%' : '',
                pointerEvents: isGroupExcluded(accessObject.group.id) ? 'none' : 'auto',
              }}
              key={accessObject.id}
              expanded={expanded === accessObject?.group?.id}
              onChange={handleChange(accessObject?.group?.id || 'emptyId')}
            >
              <AccordionSummary
                sx={{
                  '& > .MuiAccordionSummary-content': {
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  },
                  '& > .MuiAccordionSummary-expandIconWrapper': {
                    color: '#868686',
                    marginLeft: '8px',
                  },
                }}
                expandIcon={<ExpandMoreIcon />}
              >
                <div className={css.title}>{accessObject?.group?.title}</div>
                <div className={css.selectorContainer}>
                  {isGroupExcluded(accessObject.group.id) ? (
                    <>
                      <Tooltip
                        placement="right"
                        arrow
                        title={
                          <div className={css.tooltip}>
                            {' '}
                            {groupUsersOrganizer(accessObject.group as GROUP).map((u) => (
                              <div key={`${u.id}_tooltip`} style={{ display: 'flex', justifyContent: 'space-between' }}>
                                {' '}
                                <span style={{ marginRight: '8px' }}>{u?.username ? u.username : (canViewEmails ? u.email : 'User')} </span>
                              </div>
                            ))}{' '}
                          </div>
                        }
                      >
                        <div className={css.data}>
                          <img src={UsersIcon} alt="users" />
                          {groupUsersOrganizer(accessObject.group as GROUP).length}
                        </div>
                      </Tooltip>
                      <Select
                        isSearchable={false}
                        defaultValue={groupAccess(accessObject)}
                        styles={{
                          control: (baseStyles) => ({
                            ...baseStyles,
                            background: '#525252',
                            border: '1px solid #DBDBDB',
                            borderRadius: '4px',
                            color: '#DBDBDB',
                            height: '24px',
                            padding: '0 8px',
                            marginRight: '20px',
                          }),
                        }}
                        value={{
                          value: ACCESS_TYPE_ENUM.NO_ACCESS,
                          label: ACCESS_TYPE.NO_ACCESS,
                        }}
                        options={
                          accessObject.group?.subgroups?.length
                            ? GROUP_SHARE_OPTIONS
                            : GROUP_SHARE_OPTIONS.filter((o) => o.value !== ACCESS_TYPE_ENUM.SUBGROUP_SPECIFIC_ACCESS)
                        }
                        unstyled
                        className={css.customSelector}
                        classNamePrefix="select"
                        name="access"
                        onChange={(e) => {
                          onGroupAccessChange(accessObject.id, e, accessObject.group!.id, GROUP_TYPE.GROUP, accessObject);
                        }}
                        menuPosition={'fixed'}
                      />
                    </>
                  ) : (
                    <>
                      <Tooltip
                        placement="right"
                        arrow
                        title={
                          <div className={css.tooltip}>
                            {' '}
                            {groupUsersOrganizer(accessObject.group as GROUP).map((u) => (
                              <div key={`${u.id}_tooltip`} style={{ display: 'flex', justifyContent: 'space-between' }}>
                                {' '}
                                <span style={{ marginRight: '8px' }}>{u?.username ? u.username : (canViewEmails ? u.email : 'User')} </span>
                              </div>
                            ))}{' '}
                          </div>
                        }
                      >
                        <div className={css.data}>
                          <img src={UsersIcon} alt="users" />
                          {groupUsersOrganizer(accessObject.group as GROUP).length}
                        </div>
                      </Tooltip>
                      <Select
                        isSearchable={false}
                        defaultValue={groupAccess(accessObject)}
                        styles={{
                          control: (baseStyles) => ({
                            ...baseStyles,
                            background: '#525252',
                            border: '1px solid #DBDBDB',
                            borderRadius: '4px',
                            color: '#DBDBDB',
                            height: '24px',
                            padding: '0 8px',
                            marginRight: '20px',
                          }),
                        }}
                        options={
                          accessObject.group?.subgroups?.length
                            ? GROUP_SHARE_OPTIONS
                            : GROUP_SHARE_OPTIONS.filter((o) => o.value !== ACCESS_TYPE_ENUM.SUBGROUP_SPECIFIC_ACCESS)
                        }
                        unstyled
                        className={css.customSelector}
                        classNamePrefix="select"
                        name="access"
                        onChange={(e) => {
                          onGroupAccessChange(accessObject.id, e, accessObject.group!.id, GROUP_TYPE.GROUP, accessObject);
                        }}
                        menuPosition={'fixed'}
                      />
                    </>
                  )}

                  {!!(onDownloadPermissionChange && downloadPermissions) && (
                    <div className={css.allowDownloadCheckbox}>
                      <div className={css.checkboxWrapper30}>
                        <div className={css.allowDownloadCheckboxText}>
                          Allow
                          <br />
                          Downloads?
                        </div>
                        <span className={css.checkbox}>
                          <input
                            type="checkbox"
                            onClick={(e) => e.stopPropagation()}
                            onChange={() => {
                              const next = !getEffectiveAllowDownloads({ existing: accessObject, id: accessObject.group.id });
                              onDownloadPermissionChange(accessObject, accessObject.id, next);
                            }}
                            checked={
                              isGroupExcluded(accessObject.group.id)
                                ? false
                                : getEffectiveAllowDownloads({ existing: accessObject, id: accessObject.group.id })
                            }
                          />
                          <svg>
                            <use xlinkHref="#checkbox-30" className={css.checkbox}></use>
                          </svg>
                        </span>
                        <svg xmlns="http://www.w3.org/2000/svg" style={{ display: 'none' }}>
                          <symbol id="checkbox-30" viewBox="0 0 22 22">
                            <path
                              fill="none"
                              stroke="currentColor"
                              d="M5.5,11.3L9,14.8L20.2,3.3l0,0c-0.5-1-1.5-1.8-2.7-1.8h-13c-1.7,0-3,1.3-3,3v13c0,1.7,1.3,3,3,3h13 c1.7,0,3-1.3,3-3v-13c0-0.4-0.1-0.8-0.3-1.2"
                            />
                          </symbol>
                        </svg>
                      </div>
                    </div>
                  )}

                  <div className={css.actionButtons}>
                    <div className={css.removeBtn}>
                      <Tooltip title={'Delete'}>
                        <TrashButton small onClick={() => onAccessDelete(accessObject.id, 'group')} />
                      </Tooltip>
                    </div>

                    <div className={css.bellIconWrapper}>
                      <Tooltip title={idsToRenotify?.groupIds?.includes(accessObject.group.id) ? 'Re-notify on submit' : 'No notifications will be sent'}>
                        <img
                          src={idsToRenotify?.groupIds?.includes(accessObject.group.id) ? BellIconFilled : BellIcon}
                          alt="notifications"
                          className={css.groupBellIcon}
                          onClick={(e) => {
                            e.stopPropagation(); // Stop the event propagation
                            // @ts-ignore
                            addEntityToRenotify(accessObject.group.id, RenotifyIdType.GROUP, accessObject.group);
                          }}
                        />
                      </Tooltip>
                    </div>
                  </div>
                </div>
              </AccordionSummary>

              {/* Already granted sub group accesses */}
              <AccordionDetails sx={{ borderTop: '0.5px solid #868686' }}>
                {accessObject?.group?.subgroups.length === 0 && 'There are no subgroups in this group'}
                {accessObject?.group?.subgroups.map((subGroup) =>
                  isGroupExcluded(accessObject.group.id) || isGroupExcluded(subGroup.id) ? (
                    <div className={css.disableElementWrapper}>
                      <div key={subGroup.id} className={css.subGroup}>
                        <span className={css.subGroupTitle}>{subGroup.title}</span>
                        <div className={css.subGroupRightSide}>
                          <div className={css.selectorContainer}>
                            <Tooltip
                              placement="right"
                              arrow
                              title={
                                <div className={css.tooltip}>
                                  {' '}
                                  {groupUsersOrganizer(subGroup as GROUP).map((u) => (
                                    <div key={`${u.id}_tooltip`} style={{ display: 'flex', justifyContent: 'space-between' }}>
                                      {' '}
                                      <span style={{ marginRight: '8px' }}>{u?.username ? u.username : (canViewEmails ? u.email : 'User')} </span>{' '}
                                    </div>
                                  ))}{' '}
                                </div>
                              }
                            >
                              <div className={css.data}>
                                <img src={UsersIcon} alt="users" />
                                {groupUsersOrganizer(subGroup as GROUP).length}
                              </div>
                            </Tooltip>
                            <Select
                              isSearchable={false}
                              value={{
                                value: ACCESS_TYPE_ENUM.NO_ACCESS,
                                label: ACCESS_TYPE.NO_ACCESS,
                              }}
                              styles={{
                                control: (baseStyles) => ({
                                  ...baseStyles,
                                  background: '#525252',
                                  border: '1px solid #DBDBDB',
                                  borderRadius: '4px',
                                  color: '#DBDBDB',
                                  height: '24px',
                                  padding: '0 8px',
                                  marginRight: '20px',
                                }),
                              }}
                              unstyled
                              className={css.customSelector}
                              classNamePrefix="select"
                              name="access"
                              menuPosition={'fixed'}
                            />

                            {!!(onDownloadPermissionChange && downloadPermissions) && (
                              <div className={css.allowDownloadCheckbox}>
                                <div className={css.checkboxWrapper30}>
                                  <div className={css.allowDownloadCheckboxText}>
                                    Allow
                                    <br />
                                    Downloads?
                                  </div>
                                  <span className={css.checkbox}>
                                    <input type="checkbox" checked={false} readOnly />
                                    <svg>
                                      <use xlinkHref="#checkbox-30" className={css.checkbox}></use>
                                    </svg>
                                  </span>
                                  <svg xmlns="http://www.w3.org/2000/svg" style={{ display: 'none' }}>
                                    <symbol id="checkbox-30" viewBox="0 0 22 22">
                                      <path
                                        fill="none"
                                        stroke="currentColor"
                                        d="M5.5,11.3L9,14.8L20.2,3.3l0,0c-0.5-1-1.5-1.8-2.7-1.8h-13c-1.7,0-3,1.3-3,3v13c0,1.7,1.3,3,3,3h13 c1.7,0,3-1.3,3-3v-13c0-0.4-0.1-0.8-0.3-1.2"
                                      />
                                    </symbol>
                                  </svg>
                                </div>
                              </div>
                            )}

                            <div className={cs(css.actionButtons, css.subgroupActionButtons)}>
                              <div className={css.bellIconWrapper}>
                                <Tooltip title={idsToRenotify?.groupIds?.includes(subGroup.id) ? 'Re-notify on submit' : 'No notifications will be sent'}>
                                  <img
                                    src={idsToRenotify?.groupIds?.includes(subGroup.id) ? BellIconFilled : BellIcon}
                                    alt="notifications"
                                    className={css.groupBellIcon}
                                    onClick={() => addEntityToRenotify(subGroup.id, RenotifyIdType.GROUP)}
                                  />
                                </Tooltip>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div key={subGroup.id} className={css.subGroup}>
                      <span className={css.subGroupTitle}>{subGroup.title}</span>
                      <div className={css.subGroupRightSide}>
                        <div className={css.selectorContainer}>
                          <Tooltip
                            placement="right"
                            arrow
                            title={
                              <div className={css.tooltip}>
                                {' '}
                                {groupUsersOrganizer(subGroup as GROUP).map((u) => (
                                  <div key={`${u.id}_tooltip`} style={{ display: 'flex', justifyContent: 'space-between' }}>
                                    {' '}
                                    <span style={{ marginRight: '8px' }}>{u?.username ? u.username : (canViewEmails ? u.email : 'User')} </span>{' '}
                                  </div>
                                ))}{' '}
                              </div>
                            }
                          >
                            <div className={css.data}>
                              <img src={UsersIcon} alt="users" />
                              {groupUsersOrganizer(subGroup as GROUP).length}
                            </div>
                          </Tooltip>
                          <Select
                            isSearchable={false}
                            value={
                              (accessMapJson.length &&
                                SUB_GROUP_SHARE_OPTIONS.find(
                                  (opt) =>
                                    opt.value ===
                                    accessMapJson.find((g) => g.id == accessObject!.group!.id)?.subGroups.find((ac) => ac.id == subGroup.id)
                                      ?.access,
                                )) ?? {
                                value: ACCESS_TYPE_ENUM.NO_ACCESS,
                                label: ACCESS_TYPE.NO_ACCESS,
                              }
                            }
                            styles={{
                              control: (baseStyles) => ({
                                ...baseStyles,
                                background: '#525252',
                                border: '1px solid #DBDBDB',
                                borderRadius: '4px',
                                color: '#DBDBDB',
                                height: '24px',
                                padding: '0 8px',
                                marginRight: '20px',
                              }),
                            }}
                            options={SUB_GROUP_SHARE_OPTIONS}
                            unstyled
                            className={css.customSelector}
                            classNamePrefix="select"
                            name="access"
                            onChange={(e) =>
                              subGroupList.find((subgroup) => subgroup?.group?.id === subGroup.id)?.id
                                ? onGroupAccessChange(
                                    subGroupList.find((subgroup) => subgroup?.group?.id === subGroup.id)!.id,
                                    e,
                                    subGroup.id,
                                    GROUP_TYPE.SUBGROUP,
                                  )
                                : onNewGroupAccessChange(subGroup.id, e, GROUP_TYPE.SUBGROUP)
                            }
                            menuPosition={'fixed'}
                          />

                          {!!(onDownloadPermissionChange && downloadPermissions) && (
                            <div className={css.allowDownloadCheckbox}>
                              <div className={css.checkboxWrapper30}>
                                <div className={css.allowDownloadCheckboxText}>
                                  Allow
                                  <br />
                                  Downloads?
                                </div>
                                {(() => {
                                  const noAccess = isNoAccessMapped(accessObject.group.id, subGroup.id);
                                  const existingSub = findExistingSubAccess(subGroup.id);
                                  const checked = noAccess
                                    ? false
                                    : getEffectiveAllowDownloads({
                                        existing: existingSub,
                                        id: subGroup.id,
                                        parentGroupId: accessObject.group.id,
                                      });
                                  return (
                                    <span className={css.checkbox}>
                                      <input
                                        type="checkbox"
                                        onClick={(e) => e.stopPropagation()}
                                        disabled={noAccess}
                                        className={noAccess ? css.disableElementWrapper : ''}
                                        onChange={() => {
                                          if (noAccess) return;
                                          const next = !getEffectiveAllowDownloads({
                                            existing: existingSub,
                                            id: subGroup.id,
                                            parentGroupId: accessObject.group.id,
                                          });
                                          if (existingSub?.id) {
                                            onDownloadPermissionChange(existingSub, existingSub.id, next);
                                          } else if (onNewGroupDownloadPermissionChange) {
                                            onNewGroupDownloadPermissionChange(subGroup.id, next);
                                          }
                                        }}
                                        checked={checked}
                                      />
                                      <svg>
                                        <use xlinkHref="#checkbox-30" className={css.checkbox}></use>
                                      </svg>
                                    </span>
                                  );
                                })()}
                                <svg xmlns="http://www.w3.org/2000/svg" style={{ display: 'none' }}>
                                  <symbol id="checkbox-30" viewBox="0 0 22 22">
                                    <path
                                      fill="none"
                                      stroke="currentColor"
                                      d="M5.5,11.3L9,14.8L20.2,3.3l0,0c-0.5-1-1.5-1.8-2.7-1.8h-13c-1.7,0-3,1.3-3,3v13c0,1.7,1.3,3,3,3h13 c1.7,0,3-1.3,3-3v-13c0-0.4-0.1-0.8-0.3-1.2"
                                    />
                                  </symbol>
                                </svg>
                              </div>
                            </div>
                          )}

                          <div className={cs(css.actionButtons, css.subgroupActionButtons)}>
                            <div className={css.bellIconWrapper}>
                              <Tooltip title={idsToRenotify?.groupIds?.includes(subGroup.id) ? 'Re-notify on submit' : 'No notifications will be sent'}>
                                <img
                                  src={idsToRenotify?.groupIds?.includes(subGroup.id) ? BellIconFilled : BellIcon}
                                  alt="notifications"
                                  className={css.groupBellIcon}
                                  onClick={() => addEntityToRenotify(subGroup.id, RenotifyIdType.GROUP)}
                                />
                              </Tooltip>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ),
                )}
              </AccordionDetails>
            </Accordion>
          ),
      )}
    </div>
  );
};

export default ShareFolderModalGroupList;
